import React from 'react'
import { Route, useRouteMatch } from 'react-router'

// Utils
import { positionDefault } from '@reach/popover'
import { routes } from '@sketch/modules-common'
import {
  useSearchFilters,
  ShareSearchFilter,
} from 'modules/shares/hooks/useSearchFilters'
import { useSearch } from 'modules/shares/hooks/useSearch'

// Components
import {
  Dropdown,
  SelectTriggerUnstyled,
  Tooltip,
  TooltipShortcut,
} from '@sketch/components'
import { ReactComponent as ResetIcon } from '@sketch/icons/anti-clockwise-arrow-circle-16'

// Graphql
import {
  useGetDocumentListSettingsQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

// Styles
import {
  Filter as SearchInput,
  IconWrapper,
  StyledCheckbox,
  StyledChevron,
  StyledDropdownItem,
  StyledSelectPopover,
  ToggleWrapper,
} from './SearchBar.styles'
import {
  IconButton,
  StyledDropdownHeader,
  StyledSelectInput,
} from './SearchBarWithFilters.styles'

export const getPlaceholderByPath = (path: string) => {
  switch (path) {
    case routes.WORKSPACE_TRASH.template():
      return 'Search all documents in Trash'
    case routes.WORKSPACE_LIBRARIES.template():
    case routes.DESIGN_SYSTEM.template():
      return 'Search Libraries'
    case routes.WORKSPACE_SHARED_WITH_ME.template():
      return 'Search Shared with Me'
    case routes.WORKSPACE_TEMPLATES.template():
      return 'Search Templates'
    case routes.WORKSPACE_DRAFTS.template():
      return 'Search My Drafts'
    default:
      return 'Search'
  }
}

interface FiltersProps {
  userCanToggleProjects: boolean
}

const Filters: React.FC<FiltersProps> = ({
  userCanToggleProjects,
}: FiltersProps) => {
  const { filters, setFilters } = useSearchFilters()
  const { updateQuery } = useGetDocumentListSettingsQuery()
  const { path } = useRouteMatch()

  const toggleFilter = (filter: ShareSearchFilter) => {
    if (filters.includes(filter)) {
      setFilters(filters.filter(x => x !== filter))
    } else {
      setFilters([filter, ...filters])
    }
  }

  const clearFilters = () => {
    setFilters([])
  }

  const reset = () => {
    clearFilters()
    if (path === routes.WORKSPACE_SHARES.template()) {
      updateQuery(previous => ({
        ...previous,
        showDocumentsInProjects: true,
      }))
    }
  }

  return (
    <StyledSelectInput value={'Filter'} onKeyUp={() => null}>
      <SelectTriggerUnstyled>
        <ToggleWrapper>
          Filter
          <IconWrapper>
            <StyledChevron />
          </IconWrapper>
        </ToggleWrapper>
      </SelectTriggerUnstyled>
      <StyledSelectPopover position={positionDefault}>
        <StyledDropdownHeader>
          <span>Documents</span>
          <Tooltip content="Reset" placement="top">
            <IconButton onClick={reset}>
              <ResetIcon width="16px" />
            </IconButton>
          </Tooltip>
        </StyledDropdownHeader>

        <StyledDropdownItem>
          <StyledCheckbox
            checked={filters.length === 0}
            label="All documents"
            onChange={() => clearFilters()}
          />
        </StyledDropdownItem>

        <StyledDropdownItem>
          <StyledCheckbox
            checked={filters.includes('HAS_PROTOTYPES')}
            label="With prototypes"
            onChange={() => toggleFilter('HAS_PROTOTYPES')}
          />
        </StyledDropdownItem>

        <StyledDropdownItem>
          <StyledCheckbox
            checked={filters.includes('I_CAN_EDIT')}
            label="You can edit"
            onChange={() => toggleFilter('I_CAN_EDIT')}
          />
        </StyledDropdownItem>

        <FilterSettings userCanToggleProjects={userCanToggleProjects} />
      </StyledSelectPopover>
    </StyledSelectInput>
  )
}

interface FilterSettingsProps {
  userCanToggleProjects: boolean
}

const FilterSettings: React.FC<FilterSettingsProps> = ({
  userCanToggleProjects,
}: FilterSettingsProps) => {
  const { data, updateQuery } = useGetDocumentListSettingsQuery()
  const { showDocumentsInProjects } = data || {}

  if (!userCanToggleProjects) {
    return null
  }

  return (
    <Route path={routes.WORKSPACE_SHARES.template()}>
      <Dropdown.Divider />

      <StyledDropdownItem>
        <StyledCheckbox
          checked={showDocumentsInProjects}
          label="Include documents in Projects"
          onChange={() =>
            updateQuery(previous => ({
              ...previous,
              showDocumentsInProjects: !showDocumentsInProjects,
            }))
          }
        />
      </StyledDropdownItem>
    </Route>
  )
}

interface SearchBarWithFiltersProps {
  userRole: WorkspaceMinimalFragment['userRole']
}

const SearchBarWithFilters: React.FC<SearchBarWithFiltersProps> = ({
  userRole,
}: SearchBarWithFiltersProps) => {
  const { search, setSearch } = useSearch()
  const userCanToggleProjects = userRole !== 'GUEST'
  const { path } = useRouteMatch()
  const inputPlaceholder = getPlaceholderByPath(path)

  return (
    <Tooltip
      content={
        <>
          Search
          <TooltipShortcut>Press /</TooltipShortcut>
        </>
      }
      disableWhenTouchDevice
      placement="bottom-start"
    >
      <SearchInput
        onChange={setSearch}
        value={search}
        name="search"
        placeholder={inputPlaceholder}
      >
        <Route
          path={[
            routes.WORKSPACE_SHARES.template(),
            routes.WORKSPACE_DRAFTS.template(),
            routes.WORKSPACE_LIBRARIES.template(),
            routes.WORKSPACE_SHARED_WITH_ME.template(),
            routes.WORKSPACE_TEMPLATES.template(),
            routes.WORKSPACE_PROJECT.template(),
            routes.WORKSPACE_TRASH_PROJECT.template(),
            routes.WORKSPACE_COLLECTION.template(),
            routes.WORKSPACE_TRASH_COLLECTION.template(),
          ]}
        >
          <Filters userCanToggleProjects={userCanToggleProjects} />
        </Route>
      </SearchInput>
    </Tooltip>
  )
}

export default SearchBarWithFilters
